import {GroupButtonForm} from 'src/components/group-button-form';
import ReactSelect from 'src/components/react-select';
import {Form} from '@unform/web';
import {useEffect, useRef, useState} from 'react';
import {FormGroup, Input, Label} from 'reactstrap';
import {Input as UInput} from 'src/components/input';
import I18nMessage from 'src/components/I18nMessage';
import {FieldSet} from 'src/components/fieldset';
import {useCreateIntegrationConfig, useUpdateIntegrationConfig} from './mutations';
import {IntegrationConfig, IntegrationConfigCreateInput} from 'src/core/data/api/IntegrationConfig';
import {ViewType} from './container';
import {fieldsFor, integrationConfigSchema} from './required-fields';
import {Integration} from 'src/core/data/api/Integration';
import Yup from 'src/infra/Yup';
import useAlerts from 'src/hooks/use-alerts';
import {useIntl} from 'react-intl';

interface IntegrationConfigCreateOrUpdateViewProps {
    integrations: Integration[];
    isFetchingIntegrations: boolean;
    onViewChange: (viewName: ViewType, integration?: IntegrationConfig) => void;
    selectedIntegrationConfig?: IntegrationConfig;
}

const IntegrationConfigCreateOrUpdateView = ({onViewChange, integrations, isFetchingIntegrations, selectedIntegrationConfig}: IntegrationConfigCreateOrUpdateViewProps) => {
    const formRef = useRef(null);
    const {toast} = useAlerts();
    const intl = useIntl();

    const {mutate: createIntegrationConfig, isPending: isCreatePending} = useCreateIntegrationConfig();
    const {mutate: updateIntegrationConfig, isPending: isUpdatePending} = useUpdateIntegrationConfig();

    const isEditing = !!selectedIntegrationConfig;

    const [selectedIntegration, setSelectedIntegration] = useState<Integration | undefined>(
        () => integrations.find((item) => item.id === selectedIntegrationConfig?.integrationId));
    const fields = fieldsFor(selectedIntegration?.title);
    const isRequired = (name: 'username' | 'password' | 'token') => fields.required.includes(name);
    const isVisible = (name: 'username' | 'password' | 'token') => fields.visible.includes(name);

    // The integration list starts empty, so editing an existing config can only resolve it later.
    useEffect(() => {
        if (!selectedIntegration && selectedIntegrationConfig) {
            setSelectedIntegration(
                integrations.find((item) => item.id === selectedIntegrationConfig.integrationId));
        }
    }, [integrations]);

    const onSubmitError = (err: any) => {
        const validationErrors: Record<string, string> = {};
        if (err instanceof Yup.ValidationError) {
            err.inner.forEach((error) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            // @ts-ignore
            formRef.current?.setErrors(validationErrors);
            console.log('Validation Errors:', validationErrors);
        } else {
            console.log('Unexpected Error:', err);
        }
    };

    const onApiError = (error: any) => {
        toast.errorStatus(error);
    };

    const onCreateSuccess = () => {
        toast.success(intl.formatMessage({id: 'sharedSavedSuccess'}));
        onViewChange('list');
    };

    const onUpdateSuccess = () => {
        toast.success(intl.formatMessage({id: 'sharedUpdatedSuccess'}));
        onViewChange('list');
    };

    const onCancel = () => {
        onViewChange('list');
    };

    const onIntegrationChange = (option: Integration | null) => setSelectedIntegration(option ?? undefined);

    const onSubmit = (payload: any) => {
      // @ts-ignore
        formRef.current.submitForm(payload);
    };

    const handleCreate = async (data: IntegrationConfigCreateInput) => {
        const schema = integrationConfigSchema(selectedIntegration?.title);
        const result = await schema.validate(data, {abortEarly: false});
        createIntegrationConfig(result, {
            onSuccess: onCreateSuccess,
            onError: onApiError,
        });
    };

    const handleUpdate = async (data: IntegrationConfig) => {
        const schema = integrationConfigSchema(selectedIntegration?.title, {editing: true});
        const result = await schema.validate(data, {abortEarly: false});
        updateIntegrationConfig(result as IntegrationConfig, {
            onSuccess: onUpdateSuccess,
            onError: onApiError,
        });
    };

    const handleSubmit = async (data: IntegrationConfig | IntegrationConfigCreateInput) => {
        if (isEditing) {
            try {
                await handleUpdate(data as IntegrationConfig);
            } catch (err) {
                onSubmitError(err);
            }
        } else {
            try {
                await handleCreate(data);
            } catch (err) {
                onSubmitError(err);
            }
        }
    };

    return (
        <Form ref={formRef} onSubmit={handleSubmit} initialData={selectedIntegrationConfig ?? {
            username: '',
            password: '',
            token: '',
            description: '',
        }}>
            {isEditing && <UInput hidden type="number" name="id" />}
            <FieldSet className="text-left border p-4" title={intl.formatMessage({id: 'sharedIdentification'})}>
                <div className='row'>
                    <div className='col'>
                        <FormGroup className="custom-formGroup">
                            <Label for="integrationId"><I18nMessage id="sharedIntegration" />*</Label>
                            {/* @ts-ignore */}
                            <ReactSelect id="integrationId" name="integrationId" options={integrations} onChange={onIntegrationChange} />
                        </FormGroup>
                        {isVisible('username') && (
                            <FormGroup className="custom-formGroup">
                                <Label for="username"><I18nMessage id="sharedUser" />{isRequired('username') && '*'}</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="username"
                                    id="username"
                                />
                            </FormGroup>
                        )}
                        {isVisible('password') && (
                            <FormGroup className="custom-formGroup">
                                <Label for="password"><I18nMessage id="userPassword" />{isRequired('password') && '*'}</Label>
                                {/*
                                    Third party credential, not the user login: masked, and
                                    autoComplete="new-password" keeps the browser from offering
                                    to save it or filling it with the account password.
                                */}
                                <Input
                                    tag={UInput}
                                    type="password"
                                    name="password"
                                    id="password"
                                    autoComplete="new-password"
                                />
                            </FormGroup>
                        )}
                        {isVisible('token') && (
                            <FormGroup className="custom-formGroup">
                                <Label for="token"><I18nMessage id="userToken" />{isRequired('token') && '*'}</Label>
                                <Input
                                    tag={UInput}
                                    type="password"
                                    name="token"
                                    id="token"
                                    autoComplete="off"
                                />
                            </FormGroup>
                        )}
                        <FormGroup className="custom-formGroup">
                            <Label for="description"><I18nMessage id="sharedDescription" />*</Label>
                            <Input
                                tag={UInput}
                                type="text"
                                name="description"
                                id="description"
                            />
                        </FormGroup>
                    </div>
                </div>
            </FieldSet>
            <br />

            <GroupButtonForm
                loading={isCreatePending || isUpdatePending || isFetchingIntegrations}
                cancel={onCancel}
                // @ts-ignore
                submit={onSubmit}
            />
        </Form>
    );
};

export default IntegrationConfigCreateOrUpdateView;
